
import { useEffect, useRef, useState } from "react";
import { Button, Container, OffcanvasBody, OffcanvasHeader } from "reactstrap";
import styles from "./style.module.scss"
import cx from "classnames";
import DefaultOffCanvas, { DefaultOffCanvasProps } from "../DefaultOffCanvas";
import { OpenCancelInvoicePubSub, OpenCancelInvoicePubSubPayload } from "@utils/pubsubs/PubSub";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import CancelInvoiceForm from "@components/forms/CancelInvoiceForm";
import LoadingScreen from "@components/content/LoadingScreen";
import { CancelInvoiceDataType } from "@/formTypes/CancelInvoiceDataType";
import { showAlert } from "@utils/Alert";
import { isEDocumentAtvAccepted, isEDocumentAtvPending } from "@utils/edocumentAtvStatus";
import { ATV_PENDING_CANCEL_MESSAGE } from "@utils/edocumentAtvGuard";
import {
    generateCreditFromInvoice,
    sendECreditnoteData,
} from "@utils/EDocument";
import { cancelEDocumentViaLambda } from "@/api/EDocumentCancelApi";
import { CreditnoteApi, CreditnoteDataType } from "@/api/CreditnoteApi";
import { CreditnoteProductDataType } from "@/api/CreditnoteProductApi";
import { DebitnoteApi, DebitnoteDataType } from "@/api/DebitnoteApi";
import { cancelAcceptedNoteWithCounter } from "@utils/noteCancellation";
import { EasyposEInvoice } from "@/atv/submitInvoice";
import { useAppSelector } from "@/store/hooks";
import { checkCreditnoteTicketPrint } from "@utils/ticketPrintUtils";
import { CreditnoteFormDataKeys } from "@/generated/CreditnoteFormDataKeys";
import { DebitnoteFormDataKeys } from "@/generated/DebitnoteFormDataKeys";
import { InvoiceFormDataKeys } from "@/generated/InvoiceFormDataKeys";
import { CreditnoteRelatedAttributes } from "@/formTypes/CreditnoteDataType";
import { DebitnoteRelatedAttributes } from "@/formTypes/DebitnoteDataType";
import { InvoiceRelatedAttributes } from "@/formTypes/InvoiceDataType";
import DocumentReferencesEditor from "@components/forms/DocumentReferencesEditor";
import {
    StoredReference,
    buildAutoReferenceFromInvoice,
    serializeStoredReferences,
    toInfoRefElements,
} from "@utils/DocumentReferences";

type CancelInvoiceOffCanvasProps = {
    onInvoiceCancel: () => void
} & DefaultOffCanvasProps

const CancelInvoiceOffCanvas = (props: CancelInvoiceOffCanvasProps) => {
    const { btnColor = "primary", className, onInvoiceCancel } = props;
    const [invoice, setInvoice] = useState<InvoiceDataType>({} as InvoiceDataType);
    const suscriptionData = useAppSelector((state) => state.suscription.suscription);
    const [isOpen, setOpen] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [easyposEInvoice, setEasyposEInvoice] = useState<EasyposEInvoice>()
    const [manualReferences, setManualReferences] = useState<StoredReference[]>([])
    const [createCreditNoteChecked, setCreateCreditNoteChecked] = useState(false);
    const [razonText, setRazonText] = useState<string>("");
    const [cancelFormKey, setCancelFormKey] = useState(0);
    const prevNcCheckboxRef = useRef(false);
    const invoiceRef = useRef(invoice);
    invoiceRef.current = invoice;
    // `openOffCanvas` se suscribe una sola vez al montar, asi que no puede leer el
    // suscriptionData de su closure: la suscripcion puede cargarse despues.
    const suscriptionRef = useRef(suscriptionData);
    suscriptionRef.current = suscriptionData;

    useEffect(() => {
        const unsubscribe = OpenCancelInvoicePubSub.subscribe(openOffCanvas);
        if (suscriptionData) {
            setEasyposEInvoice(new EasyposEInvoice(suscriptionData))
        }
        return () => {
            unsubscribe();
        };
    }, []);

    const openOffCanvas = async (payload: OpenCancelInvoicePubSubPayload) => {
        const { openOffCanvas: shouldOpen = false } = payload;
        prevNcCheckboxRef.current = false;
        setCreateCreditNoteChecked(false);
        setRazonText("");
        setManualReferences([]);
        setCancelFormKey((k) => k + 1);

        let inv = payload.invoice || ({} as InvoiceDataType);
        if (shouldOpen && inv.id) {
            try {
                const loaded = await InvoiceApi.get(`${inv.id}`, {
                    selectionSet: ["id", "invoice_key", "fecha", "einvoice_kind", "numero", "edocument_status_payload"],
                });
                inv = { ...inv, ...loaded };
            } catch {
                /* conservar invoice del payload */
            }

            // Ultima barrera: la validacion vive en la pantalla que dispara el PubSub, pero
            // el estado puede haber cambiado desde que se pinto el listado (o el evento
            // puede venir de otro emisor).
            const einvoiceActive = Boolean(suscriptionRef.current?.einvoice_status);
            if (einvoiceActive && isEDocumentAtvPending(inv.edocument_status_payload)) {
                showAlert("danger", ATV_PENDING_CANCEL_MESSAGE);
                setInvoice({} as InvoiceDataType);
                setOpen(false);
                return;
            }
        }
        setInvoice(inv);
        setOpen(shouldOpen);
    }

    const handleCancelFormInputChange = (
        name: keyof CancelInvoiceDataType,
        value: unknown,
    ) => {
        if (name === "razon") {
            setRazonText(typeof value === "string" ? value : "");
            return;
        }
        if (name !== "createCreditNote") return;

        const checked = Boolean(value);
        setCreateCreditNoteChecked(checked);

        if (!checked) {
            setManualReferences([]);
            prevNcCheckboxRef.current = false;
            return;
        }

        if (!prevNcCheckboxRef.current) {
            prevNcCheckboxRef.current = true;
            const defaultRazon = razonText.trim() || "Anulación de factura";
            setManualReferences((prev) => {
                if (prev.length > 0) return prev;
                return [
                    buildAutoReferenceFromInvoice(
                        invoiceRef.current,
                        "anular",
                        defaultRazon,
                    ),
                ];
            });
        }
    };

    const toogleOffCanvas = (newState: boolean) => {
        setOpen(newState);
        if (newState == false) {
            onCanvasClosed();
        }
    }

    const onCanvasClosed = () => {
        setOpen(false);
    }

    const cancelInvoice = async (formData?: CancelInvoiceDataType) => {
        if (invoice.id) {
            try {
                let additionalMessage = "";
                setLoading(true)
                const selectionSet = [
                    ...InvoiceFormDataKeys.filter(k => !InvoiceRelatedAttributes.includes(k)),
                    "currency.*",
                    "client.*",
                    "invoiceProducts.*",
                    "invoiceProducts.product.*",
                    "creditnotes.*",
                    "creditnotes.creditnoteProducts.*",
                    "creditnotes.creditnoteProducts.product.*",
                    "debitnotes.*",
                    "debitnotes.debitnoteProducts.*",
                    "debitnotes.debitnoteProducts.product.*",
                ]

                const loadInvoice = await InvoiceApi.get(invoice.id, { selectionSet })

                // La lambda cancel-edocument marca la factura como anulada y devuelve el
                // inventario de forma transaccional (única fuente de verdad). Si se va a crear
                // nota de crédito, no se devuelve inventario aquí porque esa nota ya lo devuelve
                // al crearse.
                await cancelEDocumentViaLambda({
                    documentType: "Invoice",
                    documentId: `${invoice.id}`,
                    mode: "manual",
                    restock: !formData?.createCreditNote,
                    reason: formData?.razon,
                })

                const creditnoteSelectionSet = [
                    ...CreditnoteFormDataKeys.filter(k => !CreditnoteRelatedAttributes.includes(k)),
                    "currency.*",
                    "client.*",
                    "creditnoteProducts.*",
                    "creditnoteProducts.product.*",
                ];
                const debitnoteSelectionSet = [
                    ...DebitnoteFormDataKeys.filter(k => !DebitnoteRelatedAttributes.includes(k)),
                    "currency.*",
                    "client.*",
                    "debitnoteProducts.*",
                    "debitnoteProducts.product.*",
                ];

                // Anular las notas de crédito/débito ACEPTADAS de la factura creando su nota
                // contraria (NC→ND, ND→NC). El inventario ya lo revirtió la anulación de la
                // factura, por eso las contrarias van sin ajuste de stock (touchStock=false).
                const einvoiceActive = Boolean(suscriptionData?.einvoice_status);
                const buildCascadeReason = (documentKey: unknown, tipo: string): string => {
                    const base = `Anulacion de nota de ${tipo} ${documentKey} por anulacion de factura`;
                    return formData?.razon ? `${base}: ${formData.razon}` : base;
                };

                const acceptedCreditnotes = (loadInvoice.creditnotes || []).filter(
                    (cn): cn is CreditnoteDataType =>
                        Boolean(cn) &&
                        isEDocumentAtvAccepted(cn.edocument_status_payload)
                );
                const acceptedDebitnotes = (loadInvoice.debitnotes || []).filter(
                    (dn): dn is DebitnoteDataType =>
                        Boolean(dn) &&
                        isEDocumentAtvAccepted(dn.edocument_status_payload)
                );

                const debitNoteMessages: string[] = [];
                for (const cnSummary of acceptedCreditnotes) {
                    let creditnote = cnSummary;
                    if (!creditnote.creditnoteProducts?.length && creditnote.id) {
                        creditnote = await CreditnoteApi.get(creditnote.id, { selectionSet: creditnoteSelectionSet });
                    }
                    const result = await cancelAcceptedNoteWithCounter({
                        note: creditnote,
                        kind: "Creditnote",
                        reason: buildCascadeReason(creditnote.document_key, "credito"),
                        touchStock: false,
                        easyposEInvoice,
                        einvoiceActive,
                    });
                    if (result.counterNumero) debitNoteMessages.push(`#${result.counterNumero}`);
                }
                if (debitNoteMessages.length) {
                    additionalMessage += `, además se generaron notas de débito ${debitNoteMessages.join(", ")} por anulación de notas de crédito aceptadas`;
                }

                const creditNoteMessages: string[] = [];
                for (const dnSummary of acceptedDebitnotes) {
                    let debitnote = dnSummary;
                    if (!debitnote.debitnoteProducts?.length && debitnote.id) {
                        debitnote = await DebitnoteApi.get(debitnote.id, { selectionSet: debitnoteSelectionSet });
                    }
                    const result = await cancelAcceptedNoteWithCounter({
                        note: debitnote,
                        kind: "Debitnote",
                        reason: buildCascadeReason(debitnote.document_key, "debito"),
                        touchStock: false,
                        easyposEInvoice,
                        einvoiceActive,
                    });
                    if (result.counterNumero) creditNoteMessages.push(`#${result.counterNumero}`);
                }
                if (creditNoteMessages.length) {
                    additionalMessage += `, además se generaron notas de crédito ${creditNoteMessages.join(", ")} por anulación de notas de débito aceptadas`;
                }

                if (formData?.createCreditNote) {
                    const { creditnote, products } = generateCreditFromInvoice(loadInvoice, `${formData.razon}`)
                    const createdCreditnote = await CreditnoteApi.createCreditnoteAndProducts(creditnote, products as CreditnoteProductDataType[])
                    additionalMessage += `, y la nota de credito #${createdCreditnote.numero} ha sido creada existosamente`;

                    if (easyposEInvoice && suscriptionData?.einvoice_status) {
                        const creditNoteRefs: StoredReference[] =
                            manualReferences.length > 0
                                ? manualReferences
                                : [
                                    buildAutoReferenceFromInvoice(
                                        loadInvoice,
                                        "anular",
                                        `${formData.razon || createdCreditnote.description || ""}`,
                                    ),
                                ];
                        if (createdCreditnote.id) {
                            await CreditnoteApi.update(createdCreditnote.id, {
                                info_references: serializeStoredReferences(creditNoteRefs),
                            });
                        }
                        await sendECreditnoteData(
                            easyposEInvoice,
                            `${createdCreditnote.id}`,
                            toInfoRefElements(creditNoteRefs),
                        )
                    }
                    checkCreditnoteTicketPrint(suscriptionData, createdCreditnote)
                }
                showAlert("success", `La factura fue anulada correctamente${additionalMessage}`)
                onInvoiceCancel();
            } catch (err) {
                console.log("error on create creditnote", err)
                showAlert("danger", "No fue posible anular la factura")
            }
            finally {
                setLoading(false)
            }
        }
    }

    return (
        <>
            {props.children && (<Button
                color={btnColor}
                type="button"
                className={cx([className, styles.button])}
                onClick={() => toogleOffCanvas(!isOpen)}
            >
                {props.children}
            </Button>
            )}
            <DefaultOffCanvas
                className={styles.offCanvas}
                direction="end"
                isOpen={isOpen}
                onClosed={onCanvasClosed}
                toggle={() => toogleOffCanvas(!isOpen)}
            >

                <OffcanvasHeader className={styles.offCanvasHeader} toggle={onCanvasClosed}>
                    Anular Factura
                </OffcanvasHeader>
                <OffcanvasBody>
                    <Container>
                        <p>
                            ¿Está seguro de que desea anular la factura <b>#{invoice?.numero}</b>? Si marca la opción de nota de crédito, se creará una nota de crédito total y se devolverán los productos al inventario; si no la marca, solo se ajustará el inventario sin generar esa nota.
                        </p> 
                        <p>
                            Además, si la factura tiene notas de crédito aceptadas por Hacienda, el sistema generará automáticamente las notas de débito correspondientes y las enviará a validación ante Hacienda.
                        </p>
                        <CancelInvoiceForm
                            key={cancelFormKey}
                            onCancelInvoice={cancelInvoice}
                            onInputChange={handleCancelFormInputChange}
                        />
                        {createCreditNoteChecked && (
                            <>
                                <hr />
                                <h6>Referencias adicionales</h6>
                                <p className="text-muted small">
                                    Por defecto se incluye la referencia a esta factura con el código
                                    &quot;Anula documento de referencia&quot;. Puede editarla o añadir más
                                    referencias antes de confirmar la anulación.
                                </p>
                                <DocumentReferencesEditor
                                    value={manualReferences}
                                    onChange={setManualReferences}
                                />
                            </>
                        )}
                    </Container>
                </OffcanvasBody>
            </DefaultOffCanvas >
            <LoadingScreen isLoading={isLoading} />
        </>
    );
}
export default CancelInvoiceOffCanvas;
